import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { CalendarCheck, Hammer, CheckCircle2 } from "lucide-react";

const COLS = [
  { key: "planned", label: "Planned", Icon: CalendarCheck, cls: "border-violet-200 bg-violet-50 text-violet-900" },
  { key: "in_progress", label: "In progress", Icon: Hammer, cls: "border-amber-200 bg-amber-50 text-amber-900" },
  { key: "done", label: "Shipped", Icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-900" },
];
const KIND = { feature: "New feature", change: "Change", bug: "Fix", question: "Clarification" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-NZ", { month: "short", year: "numeric" }) : "");

export function PublicRoadmap() {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ["public-roadmap"], queryFn: async () => (await api.get("/roadmap/public")).data, staleTime: 300000 });
  if (!data) return null;
  const total = COLS.reduce((n, c) => n + (data[c.key]?.length || 0), 0);
  if (!total) return null;
  return (
    <section id="roadmap" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-20" data-testid="public-roadmap">
      <div className="mb-8 max-w-2xl">
        <div className="label-caps text-muted-foreground">{t("Built with our customers")}</div>
        <h2 className="mt-2 font-display text-3xl font-extrabold sm:text-4xl">{t("What's coming next")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("Every KlenaPro property can request changes from inside the app. This is what we're planning, building and have shipped from those requests.")}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {COLS.map(({ key, label, Icon, cls }) => (
          <div key={key} className={`rounded-xl border p-4 ${cls}`} data-testid={`roadmap-col-${key}`}>
            <div className="mb-3 flex items-center gap-2 font-bold"><Icon className="h-4 w-4" /> {t(label)} <span className="ml-auto text-xs font-normal opacity-70">{data[key]?.length || 0}</span></div>
            <ul className="space-y-2">
              {(data[key] || []).map((it) => (
                <li key={it.id} className="rounded-md bg-white/70 p-2.5 text-sm shadow-sm" data-testid={`roadmap-item-${it.id}`}>
                  <div className="font-medium">{it.title}</div>
                  <div className="mt-0.5 text-[11px] opacity-70">{t(KIND[it.kind] || "")}{it.when ? ` · ${fmt(it.when)}` : ""}</div>
                </li>
              ))}
              {!(data[key] || []).length && <li className="text-xs opacity-60">{t("Nothing here yet")}</li>}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function RoadmapNavLink({ className }) {
  const { data } = useQuery({ queryKey: ["public-roadmap"], queryFn: async () => (await api.get("/roadmap/public")).data, staleTime: 300000 });
  const total = data ? COLS.reduce((n, c) => n + (data[c.key]?.length || 0), 0) : 0;
  if (!total) return null;
  return <a href="#roadmap" className={className} data-testid="nav-roadmap">Roadmap</a>;
}
